/**
 * IncidentCategoriesEditor.jsx
 *
 * Governed, structured editor for incidentCategoriesByTreatmentAndCode.
 * Dimensions derived from treatmentTypes + severityCodes; subtypes inferred
 * from tree structure. Draft → publish with diff preview + audit trail.
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  deriveCascadeBlocks,
  resolveSubtypes,
  cellKey,
  getCellCategories,
  buildCodeSet,
  buildTreatmentSet,
} from '@shared/incidentCategoryCascade.js';
import {
  CONFIG_KEY,
  validateIncidentCategories,
  diffIncidentCategories,
  prepareForPublish,
  summarizeIncidentAuditDiff,
} from '../lib/incidentCategoriesUi.js';
import {
  IntegrityVerifier,
  AuditPanel,
  PublishConfirmModal,
} from './governedConfigChrome.jsx';
import { AccordionItem } from './adminPrimitives.jsx';

const clone = (x) => JSON.parse(JSON.stringify(x ?? {}));

function setCellCategories(tree, treatment, subtype, code, categories) {
  const next = clone(tree);
  if (subtype) {
    next[treatment] ||= {};
    next[treatment][subtype] ||= {};
    next[treatment][subtype][code] = categories;
  } else {
    next[treatment] ||= {};
    next[treatment][code] = categories;
  }
  return next;
}

const norm = (s) => String(s ?? '').trim().toLowerCase();

/**
 * Set the declared `subtypes` field on one treatmentTypes row. Subtypes are now
 * FIRST-CLASS on the treatment row — that is the single source of truth for
 * whether a treatment is nested, which removes the old flat-vs-nested ambiguity.
 */
function setRowSubtypes(rows, treatment, subtypes) {
  return rows.map(r => (r.value === treatment ? { ...r, subtypes } : r));
}

/**
 * Add a subtype branch to the tree. If the treatment currently holds a flat
 * (code-keyed) node, migrate those categories under the FIRST new subtype so no
 * data is lost when a flat treatment gains its first subtype.
 */
function addSubtypeToTree(tree, treatment, subtype, codes, hadSubtypes) {
  const next = clone(tree);
  const existing = next[treatment];
  const codeSet = new Set(codes);

  if (!hadSubtypes && existing && typeof existing === 'object' && !Array.isArray(existing)) {
    const keys = Object.keys(existing);
    const wasFlat = keys.length > 0 && keys.every(k => codeSet.has(k));
    if (wasFlat) {
      const migrated = {};
      for (const code of codes) migrated[code] = Array.isArray(existing[code]) ? [...existing[code]] : [];
      next[treatment] = { [subtype]: migrated };
      return next;
    }
  }

  next[treatment] ||= {};
  if (!next[treatment][subtype]) {
    next[treatment][subtype] = Object.fromEntries(codes.map(c => [c, []]));
  }
  return next;
}

function renameSubtypeInTree(tree, treatment, from, to) {
  const next = clone(tree);
  const node = next[treatment];
  if (node && typeof node === 'object' && !Array.isArray(node) && node[from] !== undefined) {
    node[to] = node[from];
    delete node[from];
  }
  return next;
}

function removeSubtypeFromTree(tree, treatment, subtype) {
  const next = clone(tree);
  const node = next[treatment];
  if (node && typeof node === 'object' && !Array.isArray(node)) {
    delete node[subtype];
    if (Object.keys(node).length === 0) delete next[treatment];
  }
  return next;
}

// ── Per-cell category list with add / edit / remove / reorder ─────────────────
function CategoryListEditor({ categories, cellPath, errors, onChange }) {
  const [editIdx, setEditIdx] = useState(null);
  const [editText, setEditText] = useState('');
  const [newText, setNewText] = useState('');
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const list = categories || [];
  const hasError = !!(errors?.length);

  function moveTo(from, to) {
    if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) return;
    const next = [...list];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  }

  function startEdit(i) {
    setEditIdx(i);
    setEditText(list[i]);
  }

  function applyEdit() {
    if (editIdx == null) return;
    const next = [...list];
    next[editIdx] = editText;
    onChange(next);
    setEditIdx(null);
    setEditText('');
  }

  function remove(i) {
    onChange(list.filter((_, j) => j !== i));
    if (editIdx === i) { setEditIdx(null); setEditText(''); }
  }

  function addCategory() {
    const s = newText.trim();
    if (!s) return;
    onChange([...list, s]);
    setNewText('');
  }

  return (
    <div style={{
      flex: 1,
      border: `1px solid ${hasError ? 'var(--danger)' : 'var(--border)'}`,
      borderRadius: 8,
      padding: '8px 10px',
      background: 'var(--surface)',
    }}>
      {list.length === 0 && (
        <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 6 }}>No categories yet</div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {list.map((cat, i) => (
          <div key={`${cellPath}-${i}`}>
            {editIdx === i ? (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input className="input" style={{ fontSize: 12, flex: 1 }}
                  value={editText} onChange={e => setEditText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') applyEdit(); if (e.key === 'Escape') setEditIdx(null); }}
                />
                <button className="btn primary sm" onClick={applyEdit} style={{ fontSize: 11 }}>Apply</button>
                <button className="btn sm" onClick={() => setEditIdx(null)} style={{ fontSize: 11 }}>Cancel</button>
              </div>
            ) : (
              <div
                draggable
                onDragStart={() => setDragIdx(i)}
                onDragOver={e => { e.preventDefault(); setOverIdx(i); }}
                onDrop={e => { e.preventDefault(); if (dragIdx != null) moveTo(dragIdx, i); setDragIdx(null); setOverIdx(null); }}
                onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '4px 6px', borderRadius: 6,
                  border: overIdx === i && dragIdx !== null && dragIdx !== i ? '1px solid var(--brand)' : '1px solid transparent',
                  opacity: dragIdx === i ? 0.5 : 1,
                }}>
                <span
                  role="button" tabIndex={0}
                  aria-label={`Reorder ${cat}`}
                  title="Drag to reorder"
                  onKeyDown={e => {
                    if (e.key === 'ArrowUp') { e.preventDefault(); moveTo(i, i - 1); }
                    if (e.key === 'ArrowDown') { e.preventDefault(); moveTo(i, i + 1); }
                  }}
                  style={{ cursor: 'grab', color: 'var(--text-3)', fontSize: 12, userSelect: 'none' }}>⠿</span>
                <span style={{ fontSize: 12, flex: 1 }}>{cat}</span>
                <button className="btn ghost sm" onClick={() => startEdit(i)} style={{ fontSize: 10 }}>Edit</button>
                <button className="btn ghost sm" onClick={() => remove(i)} style={{ fontSize: 10, color: 'var(--danger)' }}>Remove</button>
              </div>
            )}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
        <input className="input" style={{ fontSize: 12, flex: 1 }}
          placeholder="Add category…"
          value={newText}
          onChange={e => setNewText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') addCategory(); }}
        />
        <button className="btn sm" onClick={addCategory} style={{ fontSize: 11 }}>Add</button>
      </div>
      {hasError && (
        <div style={{ fontSize: 10, color: 'var(--danger)', marginTop: 6 }}>{errors.join(' ')}</div>
      )}
      {list.length > 0 && (
        <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 6 }}>
          {list.length} categor{list.length === 1 ? 'y' : 'ies'}
          {list.includes('Other') ? ' · “Other” enables free text on the form' : ''}
        </div>
      )}
    </div>
  );
}

function CellDiffPreview({ diff }) {
  const Line = ({ tag, color, children }) => (
    <li style={{ fontSize: 12, lineHeight: 1.6 }}>
      <span style={{ fontWeight: 700, color, marginRight: 6 }}>{tag}</span>{children}
    </li>
  );
  return (
    <ul style={{ margin: '8px 0 0', paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {diff.changed.map(c => (
        <Line key={c.path} tag="EDIT" color="var(--brand)">
          <strong>{c.label}</strong>
          {c.added?.length > 0 && <> — added: {c.added.join(', ')}</>}
          {c.removed?.length > 0 && <> — removed: {c.removed.join(', ')}</>}
          {c.reordered && <> — reordered</>}
        </Line>
      ))}
    </ul>
  );
}

// ── Inline subtype manager — add / rename / remove (replaces window.prompt) ───
function SubtypeManager({ treatment, subtypes, onAdd, onRename, onRemove }) {
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [editing, setEditing] = useState(null); // subtype being renamed
  const [editName, setEditName] = useState('');
  const [removing, setRemoving] = useState(null); // subtype pending removal choice

  function commitAdd() {
    const s = newName.trim();
    if (s) onAdd(s);
    setNewName(''); setAdding(false);
  }
  function commitRename() {
    const s = editName.trim();
    if (s && s !== editing) onRename(editing, s);
    setEditing(null); setEditName('');
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      {subtypes.map(s => (
        editing === s ? (
          <span key={s} style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
            <input className="input" style={{ fontSize: 11, width: 110 }}
              value={editName} onChange={e => setEditName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setEditing(null); }}
              autoFocus />
            <button className="btn primary sm" style={{ fontSize: 10 }} onClick={commitRename}>Save</button>
            <button className="btn sm" style={{ fontSize: 10 }} onClick={() => setEditing(null)}>Cancel</button>
          </span>
        ) : removing === s ? (
          // Deliberate keep-or-delete — never a silent prune. "Keep data" stashes
          // the subtype's categories so re-adding the subtype restores them;
          // "Delete" discards them. Either way the published tree stays clean.
          <span key={s} className="badge" style={{ display: 'inline-flex', gap: 4, alignItems: 'center', fontSize: 11, border: '1px solid var(--danger)' }}>
            Remove “{s}”?
            <button className="btn ghost sm" style={{ fontSize: 10, padding: '0 6px' }}
              title="Remove the subtype but keep its categories (re-add the subtype to restore them)"
              onClick={() => { onRemove(s, false); setRemoving(null); }}>Keep data</button>
            <button className="btn ghost sm" style={{ fontSize: 10, padding: '0 6px', color: 'var(--danger)' }}
              title="Remove the subtype AND delete its categories"
              onClick={() => { onRemove(s, true); setRemoving(null); }}>Delete</button>
            <button className="btn ghost sm" style={{ fontSize: 10, padding: '0 6px' }}
              onClick={() => setRemoving(null)}>Cancel</button>
          </span>
        ) : (
          <span key={s} className="badge" style={{ display: 'inline-flex', gap: 4, alignItems: 'center', fontSize: 11 }}>
            {s}
            <button className="btn ghost sm" style={{ fontSize: 9, padding: '0 4px' }}
              title={`Rename ${s}`}
              onClick={() => { setEditing(s); setEditName(s); }}>✎</button>
            <button className="btn ghost sm" style={{ fontSize: 9, padding: '0 4px', color: 'var(--danger)' }}
              title={`Remove ${s}`}
              onClick={() => setRemoving(s)}>✕</button>
          </span>
        )
      ))}
      {adding ? (
        <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
          <input className="input" style={{ fontSize: 11, width: 120 }}
            placeholder="Subtype name…"
            value={newName} onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') commitAdd(); if (e.key === 'Escape') { setAdding(false); setNewName(''); } }}
            autoFocus />
          <button className="btn primary sm" style={{ fontSize: 10 }} onClick={commitAdd}>Add</button>
          <button className="btn sm" style={{ fontSize: 10 }} onClick={() => { setAdding(false); setNewName(''); }}>Cancel</button>
        </span>
      ) : (
        <button className="btn ghost sm" style={{ fontSize: 11 }} onClick={() => setAdding(true)}>+ Add subtype</button>
      )}
    </div>
  );
}

export default function IncidentCategoriesEditor({ tree: published, treatmentTypes, severityCodes, onPublishBatch }) {
  const [draft, setDraft]         = useState(() => clone(published));
  // Subtypes are first-class on treatmentTypes, so the editor keeps a draft of
  // the treatment rows too. Editing a subtype publishes BOTH keys atomically.
  const [draftTreatments, setDraftTreatments] = useState(() => clone(treatmentTypes));
  // "Remove subtype (keep data)" stashes the branch here (keyed treatment→subtype)
  // so the published tree never carries an undeclared orphan, yet re-adding the
  // subtype restores its categories — a deliberate keep, no silent data loss.
  const [stashedBranches, setStashedBranches] = useState({});
  const [dirty, setDirty]         = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showAudit, setShowAudit]   = useState(false);
  const [auditToken, setAuditToken] = useState(0);
  // One-at-a-time accordion: only the open treatment renders its (heavy) body,
  // so the page is never a 10k-px monolith regardless of treatment count.
  const [openTreatment, setOpenTreatment] = useState(null);

  useEffect(() => {
    if (!dirty) { setDraft(clone(published)); setDraftTreatments(clone(treatmentTypes)); }
  }, [published, treatmentTypes]); // eslint-disable-line react-hooks/exhaustive-deps

  const codeSet = useMemo(() => buildCodeSet(severityCodes), [severityCodes]);
  const codes   = useMemo(() => [...codeSet], [codeSet]);
  const blocks = useMemo(
    () => deriveCascadeBlocks(draftTreatments, severityCodes, draft),
    [draftTreatments, severityCodes, draft],
  );
  // Validate against the tree minus orphaned branches — i.e. top-level keys for
  // treatments that no longer exist (e.g. a treatment/subtype was deleted after
  // its categories were saved). Publish (normalizeCategoryTree) already prunes
  // these, so they must not hard-block publishing; otherwise the orphan is
  // unreachable from the UI and the editor deadlocks. Cells stay raw so the
  // empty/duplicate-category checks still fire.
  const validatableTree = useMemo(() => {
    const known = buildTreatmentSet(draftTreatments);
    const out = {};
    for (const [treatment, node] of Object.entries(draft || {})) {
      if (known.has(treatment)) out[treatment] = node;
    }
    return out;
  }, [draft, draftTreatments]);
  const validation = useMemo(
    () => validateIncidentCategories(validatableTree, draftTreatments, severityCodes),
    [validatableTree, draftTreatments, severityCodes],
  );
  const diff = useMemo(
    () => diffIncidentCategories(published, draft, draftTreatments, severityCodes),
    [published, draft, draftTreatments, severityCodes],
  );

  const cellErrors = validation.byCell || validation.byId || {};

  function updateCell(treatment, subtype, code, categories) {
    setDraft(prev => setCellCategories(prev, treatment, subtype, code, categories));
    setDirty(true);
  }

  function currentSubtypes(treatment) {
    const row = draftTreatments.find(t => t.value === treatment);
    return resolveSubtypes(row); // declared field is the single source of truth
  }

  function addSubtype(treatment, name) {
    const subtype = name.trim();
    const existing = currentSubtypes(treatment);
    if (existing.some(s => norm(s) === norm(subtype))) return; // dedupe
    const hadSubtypes = existing.length > 0;
    // Restore a previously-kept branch if one was stashed under this name.
    const stashKey = `${treatment}|${subtype}`;
    const stashed = stashedBranches[stashKey];
    setDraft(prev => {
      if (stashed) {
        const next = clone(prev);
        next[treatment] ||= {};
        next[treatment][subtype] = clone(stashed);
        return next;
      }
      return addSubtypeToTree(prev, treatment, subtype, codes, hadSubtypes);
    });
    if (stashed) setStashedBranches(prev => { const n = { ...prev }; delete n[stashKey]; return n; });
    setDraftTreatments(prev => setRowSubtypes(prev, treatment, [...existing, subtype]));
    setDirty(true);
  }

  function renameSubtype(treatment, from, to) {
    const existing = currentSubtypes(treatment);
    if (existing.some(s => norm(s) === norm(to))) return; // dedupe target
    setDraft(prev => renameSubtypeInTree(prev, treatment, from, to));
    setDraftTreatments(prev => setRowSubtypes(prev, treatment, existing.map(s => (s === from ? to : s))));
    setDirty(true);
  }

  // Removing a subtype is DELIBERATE about its data — it never silently prunes.
  // The user picks (SubtypeManager): `deleteBranch=true` discards the categories;
  // `false` ("keep data") stashes the branch so re-adding the subtype restores
  // it. EITHER way the branch leaves the active tree, so the published tree never
  // carries an undeclared orphan and stays valid against the declared model.
  function removeSubtype(treatment, subtype, deleteBranch) {
    const existing = currentSubtypes(treatment);
    if (!deleteBranch) {
      const branch = draft?.[treatment]?.[subtype];
      if (branch && typeof branch === 'object') {
        setStashedBranches(prev => ({ ...prev, [`${treatment}|${subtype}`]: clone(branch) }));
      }
    }
    setDraft(prev => removeSubtypeFromTree(prev, treatment, subtype));
    setDraftTreatments(prev => setRowSubtypes(prev, treatment, existing.filter(s => s !== subtype)));
    setDirty(true);
  }

  function discard() {
    setDraft(clone(published));
    setDraftTreatments(clone(treatmentTypes));
    setStashedBranches({});
    setDirty(false);
    setPreviewing(false);
  }

  async function publish() {
    setPublishing(true);
    try {
      // ATOMIC publish: treatmentTypes (the declared-subtype source of truth) and
      // the category tree go out in ONE batch transaction so a failure can never
      // desync declared subtypes from their tree branches.
      //
      // treatmentTypes is ALWAYS sent (not only when changed): the server
      // validates the tree's treatment keys against the treatmentTypes in the
      // SAME batch, falling back to the stored DB row otherwise. `draftTreatments`
      // is the effective list (code defaults merged in — e.g. the workbook
      // treatments), so co-sending it both supplies the correct validation
      // context and reconciles a stale persisted treatmentTypes. Omitting it made
      // a tree that references a code-default treatment fail with
      // `Unknown treatment "…"` when the DB row predated that treatment.
      const normalized = prepareForPublish(draft, draftTreatments);
      const entries = [
        { key: 'treatmentTypes', value: draftTreatments },
        { key: CONFIG_KEY,       value: normalized },
      ];
      await onPublishBatch(entries, { publish: true });
      setDraft(normalized);
      setDirty(false);
      setPreviewing(false);
      setAuditToken(t => t + 1);
    } finally {
      setPublishing(false);
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>
            Incident Categories
            {dirty && <span className="badge" style={{ marginLeft: 8, fontSize: 10 }}>Draft</span>}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>
            Options shown in the new-case cascade, per treatment and severity code. Treatments and codes come from your configured taxonomies.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button className="btn ghost sm" onClick={() => setShowAudit(s => !s)} style={{ fontSize: 11 }}>
            {showAudit ? 'Hide history' : 'History'}
          </button>
          {dirty && (
            <button className="btn primary" onClick={() => setPreviewing(true)} disabled={!validation.ok}
              title={validation.ok ? 'Review and publish' : 'Fix validation errors first'} style={{ fontSize: 12 }}>
              Review &amp; publish
            </button>
          )}
        </div>
      </div>

      {!validation.ok && (
        <div className="callout danger" style={{ marginBottom: 12 }}>
          <div style={{ flex: 1, fontSize: 12 }}>
            <strong>Cannot publish yet:</strong>
            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
              {validation.errors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          </div>
        </div>
      )}

      {showAudit && (
        <div style={{ marginBottom: 14, padding: '12px 14px', border: '1px solid var(--border)', borderRadius: 10, background: 'var(--surface-2)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Change history</div>
          <IntegrityVerifier />
          <AuditPanel configKey={CONFIG_KEY} refreshToken={auditToken} summarizeDiff={summarizeIncidentAuditDiff} />
        </div>
      )}

      {blocks.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--text-3)', padding: '12px 0' }}>
          No treatments configured yet. Add treatment types and severity codes first.
        </div>
      )}

      {/* Treatments collapse into an accordion (one open at a time, body rendered
          lazily) so the editor never grows into a multi-thousand-px monolith. */}
      <div>
        {blocks.map(block => {
          const open = openTreatment === block.treatment;
          // Surface a per-treatment summary + an error flag on the collapsed
          // header so a validation error in a closed treatment is still visible.
          const blockHasError = Object.keys(cellErrors).some(
            p => p === block.treatment || p.startsWith(`${block.treatment}|`),
          );
          const subtypeCount = block.subtypes ? block.subtypes.length : 0;
          const meta = (
            <>
              {subtypeCount > 0 ? `${subtypeCount} subtype${subtypeCount === 1 ? '' : 's'} · ` : ''}
              {block.codes.length} code{block.codes.length === 1 ? '' : 's'}
              {blockHasError && <span style={{ color: 'var(--danger)', fontWeight: 600 }}> · needs attention</span>}
            </>
          );
          return (
            <AccordionItem
              key={block.treatment}
              title={block.label}
              meta={meta}
              open={open}
              onToggle={() => setOpenTreatment(open ? null : block.treatment)}
            >
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
                <SubtypeManager
                  treatment={block.treatment}
                  subtypes={block.subtypes || []}
                  onAdd={name => addSubtype(block.treatment, name)}
                  onRename={(from, to) => renameSubtype(block.treatment, from, to)}
                  onRemove={(s, deleteBranch) => removeSubtype(block.treatment, s, deleteBranch)}
                />
              </div>

              {block.subtypes ? (
                block.subtypes.map(subtype => (
                  <div key={subtype} style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 8 }}>{subtype}</div>
                    <div className="code-cell-grid">
                      {block.codes.map(code => {
                        const path = cellKey(block.treatment, subtype, code);
                        const cats = getCellCategories(draft, block.treatment, subtype, code, codeSet);
                        return (
                          <div key={path} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)' }}>{code}</label>
                            <CategoryListEditor
                              categories={cats}
                              cellPath={path}
                              errors={cellErrors[path]}
                              onChange={list => updateCell(block.treatment, subtype, code, list)}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              ) : (
                <div className="code-cell-grid">
                  {block.codes.map(code => {
                    const path = cellKey(block.treatment, null, code);
                    const cats = getCellCategories(draft, block.treatment, null, code, codeSet);
                    return (
                      <div key={path} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)' }}>{code}</label>
                        <CategoryListEditor
                          categories={cats}
                          cellPath={path}
                          errors={cellErrors[path]}
                          onChange={list => updateCell(block.treatment, null, code, list)}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </AccordionItem>
          );
        })}
      </div>

      <PublishConfirmModal
        open={previewing}
        title="Publish Incident Categories?"
        description="These changes affect how every adverse event is classified. Review the diff before publishing."
        publishing={publishing}
        hasChanges={diff.hasChanges}
        canPublish={validation.ok}
        onCancel={() => setPreviewing(false)}
        onDiscard={discard}
        onPublish={publish}
      >
        <CellDiffPreview diff={diff} />
      </PublishConfirmModal>
    </div>
  );
}
